"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"; 
import { apiPath } from "@/lib/apiPath";

type CartContextValue = {
  itemsCount: number;
  isHydrated: boolean;
  setItemsCount: (n: number) => void;
  refreshCart: () => Promise<void>;
};

const CartContext = createContext<CartContextValue | null>(null);

function toSafeCount(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

export function CartProvider({
  children,
  initialCount,
  lang = "",
}: {
  children: React.ReactNode;
  initialCount?: number;
  lang?: string;
}) {
  const initial = toSafeCount(initialCount);

  const [itemsCount, _setItemsCount] = useState<number>(initial);
  const [isHydrated, setIsHydrated] = useState<boolean>(initialCount !== undefined);

  const didAutoFetch = useRef(false);
  const prevLang = useRef(lang);

  const refreshSeqRef = useRef(0);
  const localWriteSeqRef = useRef(0);

  function commitCount(n: number) {
    _setItemsCount(toSafeCount(n));
    setIsHydrated(true);
  }

  function setItemsCount(n: number) {
    localWriteSeqRef.current += 1;
    commitCount(n);
  }

  async function refreshCart() {
    const refreshId = ++refreshSeqRef.current;
    const localWriteSeqAtStart = localWriteSeqRef.current;

    try {
      const res = await fetch(apiPath("/api/wc/cart/get"), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(lang ? { "X-Lang": lang } : {}),
        },
        cache: "no-store",
        credentials: "include",
      });

      if (!res.ok) return;

      const cart = (await res.json().catch(() => null)) as { items_count?: unknown } | null;
      const nextCount = toSafeCount(cart?.items_count);

      if (refreshId !== refreshSeqRef.current) return;
      if (localWriteSeqRef.current !== localWriteSeqAtStart) return;

      commitCount(nextCount);
    } catch {
      // ignore
    } finally {
      if (refreshId === refreshSeqRef.current) {
        setIsHydrated(true);
      }
    }
  }

  useEffect(() => {
    if (isHydrated) return;
    if (didAutoFetch.current) return;

    didAutoFetch.current = true;
    void refreshCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated]);

  useEffect(() => {
    if (prevLang.current === lang) return;

    prevLang.current = lang;
    if (!lang) return;

    void refreshCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const value = useMemo<CartContextValue>(
    () => ({
      itemsCount,
      isHydrated,
      setItemsCount,
      refreshCart,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsCount, isHydrated]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside <CartProvider />");
  }
  return ctx;
}